import FloodOutlinedIcon from '@mui/icons-material/FloodOutlined'
import WavesOutlinedIcon from '@mui/icons-material/WavesOutlined'
import AccountBalanceOutlinedIcon from '@mui/icons-material/AccountBalanceOutlined'
import HomeWorkOutlinedIcon from '@mui/icons-material/HomeWorkOutlined'
import LocationCityOutlinedIcon from '@mui/icons-material/LocationCityOutlined'
import CastleOutlinedIcon from '@mui/icons-material/CastleOutlined'
import MuseumOutlinedIcon from '@mui/icons-material/MuseumOutlined'
import ReportProblemOutlinedIcon from '@mui/icons-material/ReportProblemOutlined'
import PublicOutlinedIcon from '@mui/icons-material/PublicOutlined'
import YardOutlinedIcon from '@mui/icons-material/YardOutlined'
import ShieldOutlinedIcon from '@mui/icons-material/ShieldOutlined'
import HistoryEduOutlinedIcon from '@mui/icons-material/HistoryEduOutlined'
import RuleOutlinedIcon from '@mui/icons-material/RuleOutlined'
import BusinessOutlinedIcon from '@mui/icons-material/BusinessOutlined'
import GroupsOutlinedIcon from '@mui/icons-material/GroupsOutlined'
import FactoryOutlinedIcon from '@mui/icons-material/FactoryOutlined'
import AirOutlinedIcon from '@mui/icons-material/AirOutlined'
import GrassOutlinedIcon from '@mui/icons-material/GrassOutlined'
import WaterDropOutlinedIcon from '@mui/icons-material/WaterDropOutlined'
import TerrainOutlinedIcon from '@mui/icons-material/TerrainOutlined'
import LandscapeOutlinedIcon from '@mui/icons-material/LandscapeOutlined'
import ScienceOutlinedIcon from '@mui/icons-material/ScienceOutlined'
import EmojiNatureOutlinedIcon from '@mui/icons-material/EmojiNatureOutlined'
import SpaOutlinedIcon from '@mui/icons-material/SpaOutlined'
import ForestOutlinedIcon from '@mui/icons-material/ForestOutlined'
import NatureOutlinedIcon from '@mui/icons-material/NatureOutlined'
import LocalFloristOutlinedIcon from '@mui/icons-material/LocalFloristOutlined'
import PolicyOutlinedIcon from '@mui/icons-material/PolicyOutlined'

/**
 * Presentation metadata for the planning.data.gov.uk screening. The API tells
 * us label / status / severity per dataset; this file only maps datasets to
 * icons and severities to colours so the tiles read at a glance.
 */

export const DATASET_ICONS = {
  'flood-risk-zone': FloodOutlinedIcon,
  'flood-storage-area': WavesOutlinedIcon,
  'listed-building': AccountBalanceOutlinedIcon,
  'locally-listed-building': HomeWorkOutlinedIcon,
  'building-preservation-notice': ShieldOutlinedIcon,
  'conservation-area': LocationCityOutlinedIcon,
  'scheduled-monument': CastleOutlinedIcon,
  'heritage-at-risk': ReportProblemOutlinedIcon,
  'world-heritage-site': PublicOutlinedIcon,
  'world-heritage-site-buffer-zone': PublicOutlinedIcon,
  'park-and-garden': YardOutlinedIcon,
  battlefield: MuseumOutlinedIcon,
  'archaeological-priority-area': HistoryEduOutlinedIcon,
  'article-4-direction-area': RuleOutlinedIcon,
  'central-activities-zone': BusinessOutlinedIcon,
  'asset-of-community-value': GroupsOutlinedIcon,
  'brownfield-land': FactoryOutlinedIcon,
  'air-quality-management-area': AirOutlinedIcon,
  'green-belt': GrassOutlinedIcon,
  'nutrient-neutrality-catchment': WaterDropOutlinedIcon,
  'national-park': TerrainOutlinedIcon,
  'area-of-outstanding-natural-beauty': LandscapeOutlinedIcon,
  'site-of-special-scientific-interest': ScienceOutlinedIcon,
  'special-area-of-conservation': EmojiNatureOutlinedIcon,
  'special-protection-area': SpaOutlinedIcon,
  ramsar: WaterDropOutlinedIcon,
  'national-nature-reserve': ForestOutlinedIcon,
  'local-nature-reserve': NatureOutlinedIcon,
  'ancient-woodland': ForestOutlinedIcon,
  'tree-preservation-zone': LocalFloristOutlinedIcon
}

export const GROUP_ICONS = {
  flood: FloodOutlinedIcon,
  heritage: AccountBalanceOutlinedIcon,
  planning: PolicyOutlinedIcon,
  environment: AirOutlinedIcon,
  ecology: ForestOutlinedIcon
}

// Tone = how a tile is painted. Derived from status first, then severity.
export const toneFor = (status, severity) => {
  if (status === 'flagged') {
    if (severity === 'critical') return 'crit'
    if (severity === 'warning') return 'warn'
    return 'info'
  }
  if (status === 'historic') return 'muted'
  if (status === 'clear') return 'ok'
  return 'neutral' // none_recorded
}

export const TONES = {
  crit: { bg: '#fef2f2', border: '#fecaca', fg: '#b91c1c', bar: '#dc2626', label: 'Critical' },
  warn: { bg: '#fff7ed', border: '#fed7aa', fg: '#c2410c', bar: '#f97316', label: 'Warning' },
  info: { bg: '#eff6ff', border: '#bfdbfe', fg: '#1d4ed8', bar: '#3b82f6', label: 'Note' },
  ok: { bg: '#ecfdf5', border: '#a7f3d0', fg: '#065f46', bar: '#10b981', label: 'Clear' },
  muted: { bg: '#f8fafc', border: '#e2e8f0', fg: '#64748b', bar: '#94a3b8', label: 'Historic' },
  neutral: { bg: '#fcfdfe', border: '#f2f2f2', fg: '#6b7280', bar: '#d1d5db', label: 'None recorded' }
}

/** Read a surfaced planning.data.gov.uk attribute off an entity (properties is a [{key,label,value}] list). */
export const getProp = (entity, key) => entity?.properties?.find(p => p.key === key)?.value

const FLOOD_LEVELS = {
  2: 'Zone 2 · medium probability',
  3: 'Zone 3 · high probability'
}

/** Short status line shown on a tile, e.g. "Grade I", "Zone 3 · high probability". */
export const tileStatusLabel = item => {
  const { status, count, entities = [], dataset, coverage } = item

  if (status === 'clear') return 'Clear'
  if (status === 'none_recorded') return coverage === 'partial' ? 'None recorded' : 'Clear'
  if (status === 'historic') {
    const until = entities.find(e => e.isHistoric)?.endDate
    return until ? `Formerly · until ${formatShortDate(until)}` : 'Formerly'
  }

  const active = entities.filter(e => !e.isHistoric)

  if (dataset === 'flood-risk-zone') {
    const levels = [...new Set(active.map(e => getProp(e, 'flood-risk-level')).filter(Boolean))].sort()
    const worst = levels[levels.length - 1]
    return FLOOD_LEVELS[worst] || `Zone ${worst || '?'}`
  }

  if (dataset === 'listed-building') {
    const grades = [...new Set(active.map(e => getProp(e, 'listed-building-grade')).filter(Boolean))]
    if (grades.length) return `Grade ${grades.join(' / ')}`
  }

  if (dataset === 'park-and-garden') {
    const grade = getProp(active[0], 'park-and-garden-grade')
    if (grade) return `Grade ${grade}`
  }

  if (count > 1) return `${count} designations`
  return active[0]?.name || 'Within area'
}

export const formatShortDate = value => {
  if (!value) return ''
  const d = new Date(value)
  if (Number.isNaN(d.getTime())) return value
  return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })
}

export const formatRelative = iso => {
  if (!iso) return null
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return null
  const diffMs = Date.now() - d.getTime()
  const mins = Math.round(diffMs / 60000)
  if (mins < 1) return 'just now'
  if (mins < 60) return `${mins} min ago`
  const hours = Math.round(mins / 60)
  if (hours < 24) return `${hours} h ago`
  const days = Math.round(hours / 24)
  if (days < 30) return `${days} day${days === 1 ? '' : 's'} ago`
  return formatShortDate(iso)
}


/**
 * Colours used by the planning.data.gov.uk map for each dataset (taken from
 * their `paint_options`), so our map reads the same as theirs. Datasets they
 * leave on the default blue get a distinct colour here.
 */
export const DATASET_COLOURS = {
  'flood-risk-zone': '#003078',
  'flood-storage-area': '#1d70b8',
  'listed-building': '#F9C744',
  'listed-building-outline': '#F9C744',
  'locally-listed-building': '#e0a800',
  'building-preservation-notice': '#f944c7',
  'conservation-area': '#78AA00',
  'scheduled-monument': '#0F9CDA',
  'heritage-at-risk': '#8D73AF',
  'world-heritage-site': '#EB1EE5',
  'world-heritage-site-buffer-zone': '#f58ef1',
  'park-and-garden': '#0EB951',
  battlefield: '#4d2942',
  'archaeological-priority-area': '#b54405',
  'article-4-direction-area': '#d4351c',
  'central-activities-zone': '#5694ca',
  'asset-of-community-value': '#f47738',
  'brownfield-land': '#745729',
  'air-quality-management-area': '#87823A',
  'green-belt': '#85994b',
  'nutrient-neutrality-catchment': '#308fac',
  'national-park': '#3DA52C',
  'area-of-outstanding-natural-beauty': '#d53880',
  'site-of-special-scientific-interest': '#308fac',
  'special-area-of-conservation': '#7A8705',
  'special-protection-area': '#28a197',
  ramsar: '#2E8B8B',
  'national-nature-reserve': '#00703c',
  'local-nature-reserve': '#5fb25f',
  'ancient-woodland': '#00703c',
  'tree-preservation-zone': '#6da356'
}

export const datasetColour = dataset => DATASET_COLOURS[dataset] || '#003078'
